import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { MASTER_CURRENCY } from '@/graphql/queries';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrency } from '@/redux/currencySlice';
import { cn } from '@/lib/utils';

/* ─── Inject keyframes once ─────────────────────────────────────────── */
const STYLE_ID = 'currency-dropdown-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Montserrat:wght@400;500;600;700&display=swap');

    .cd-panel {
      animation: cd-appear 0.22s cubic-bezier(0.16, 1, 0.3, 1) both;
      transform-origin: top right;
    }
    @keyframes cd-appear {
      from { opacity: 0; transform: scale(0.94) translateY(-6px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);    }
    }

    .cd-item {
      transition: background 0.15s ease, padding-left 0.18s ease;
    }
    .cd-item:hover {
      padding-left: 18px;
    }

    .cd-symbol {
      transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
    }
    .cd-item:hover .cd-symbol {
      transform: scale(1.08);
    }

    .cd-trigger-inner {
      transition: letter-spacing 0.2s ease;
    }
    .cd-trigger:hover .cd-trigger-inner {
      letter-spacing: 0.08em;
    }

    .cd-scroll::-webkit-scrollbar { width: 3px; }
    .cd-scroll::-webkit-scrollbar-track { background: transparent; }
    .cd-scroll::-webkit-scrollbar-thumb {
      background: #d6bfae;
      border-radius: 99px;
    }

    .cd-shimmer {
      background: linear-gradient(110deg, #f5ede4 30%, #fdf7f2 50%, #f5ede4 70%);
      background-size: 200% 100%;
      animation: cd-shimmer 1.4s infinite linear;
      border-radius: 8px;
    }
    @keyframes cd-shimmer {
      from { background-position: 200% 0; }
      to   { background-position: -200% 0; }
    }

    .cd-chevron {
      transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease;
    }
    .cd-chevron.open { transform: rotate(180deg); }
  `;
  document.head.appendChild(style);
}

/* ─── Component ──────────────────────────────────────────────────────── */
const CurrencyDropdown = ({ shouldBeWhite = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const { loading, error, data } = useQuery(MASTER_CURRENCY);
  const currencies = data?.master_currency || [];
  const hasSetCurrencyOnce = useRef(false);

  const handleSelect = (currency) => {
    dispatch(setCurrency(currency));
    setIsOpen(false);
  };
const COUNTRY_CODE_CURRENCY = {
  // INR — South Asia
  in: 'INR', np: 'INR', lk: 'INR', bd: 'INR', pk: 'INR',

  // GBP — United Kingdom & territories
  gb: 'GBP', je: 'GBP', gg: 'GBP', im: 'GBP', // Jersey, Guernsey, Isle of Man
  fk: 'GBP', gi: 'GBP', sh: 'GBP', tc: 'GBP', // Falklands, Gibraltar, St Helena, Turks & Caicos
  io: 'GBP', gs: 'GBP',                         // British Indian Ocean Territory, South Georgia

  // CAD — Canada only
  ca: 'CAD',

  // AUD — Australia & territories that use AUD
  au: 'AUD', nz: 'AUD', fj: 'AUD', pg: 'AUD',
  ck: 'AUD', nu: 'AUD', tv: 'AUD', nr: 'AUD',  // Cook Islands, Niue, Tuvalu, Nauru
  cx: 'AUD', cc: 'AUD', hm: 'AUD', nf: 'AUD',  // Christmas, Cocos, Heard, Norfolk Islands
  ki: 'AUD', sb: 'AUD', vu: 'AUD', ws: 'AUD',  // Kiribati, Solomon Islands, Vanuatu, Samoa
  nz:"AUD",                                    // New Zealand

  // EURO — Eurozone members
  de: 'EURO', fr: 'EURO', it: 'EURO', es: 'EURO', pt: 'EURO',
  nl: 'EURO', be: 'EURO', at: 'EURO', fi: 'EURO', gr: 'EURO',
  ie: 'EURO', lu: 'EURO', mt: 'EURO', cy: 'EURO', si: 'EURO',
  sk: 'EURO', ee: 'EURO', lv: 'EURO', lt: 'EURO', hr: 'EURO',

  // EURO — Non-EU countries that officially use EURO
  ad: 'EURO', mc: 'EURO', sm: 'EURO', va: 'EURO',  // Andorra, Monaco, San Marino, Vatican
  me: 'EURO', xk: 'EURO',                         // Montenegro, Kosovo

  // EURO — French overseas territories
  gp: 'EURO', mq: 'EURO', gf: 'EURO', re: 'EURO', yt: 'EURO', // Guadeloupe, Martinique, French Guiana, Réunion, Mayotte
  pm: 'EURO', bl: 'EURO', mf: 'EURO', tf: 'EURO', wf: 'EURO', // St Pierre, St Barthélemy, St Martin, French Southern, Wallis
  pf: 'EURO', nc: 'EURO',                                    // French Polynesia, New Caledonia

  // EURO — Other EU members (non-Eurozone but EURO widely accepted / mapped here)
  pl: 'EURO', se: 'EURO', dk: 'EURO', no: 'EURO', ch: 'EURO',
  cz: 'EURO', hu: 'EURO', ro: 'EURO', bg: 'EURO',
};
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
useEffect(() => {
  if (hasSetCurrencyOnce.current) return;
  if (!selectedCurrency && currencies.length > 0) {

    const findCurrency = (name) =>
      currencies.find((c) => c.name?.toLowerCase() === name?.toLowerCase());

const setDefault = () => {
  dispatch(setCurrency(findCurrency('USD') || currencies[0]));
  hasSetCurrencyOnce.current = true;
};

const applyCountryCode = (countryCode) => {
  const currencyName = countryCode ? COUNTRY_CODE_CURRENCY[countryCode] : null;
  const matched = currencyName ? findCurrency(currencyName) : null;
  
  console.log('Country code:', countryCode);
  console.log('Expected currency name:', currencyName);
  console.log('Matched currency:', matched);
  console.log('Available currencies:', currencies.map(c => c.name));
  
  dispatch(setCurrency(matched || findCurrency('USD') || currencies[0]));
  hasSetCurrencyOnce.current = true;
};
    const tryGPS = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse` +
                `?lat=${position.coords.latitude}` +
                `&lon=${position.coords.longitude}&format=json`
              );
              const geo = await res.json();
              const countryCode = geo?.address?.country_code?.toLowerCase();
              applyCountryCode(countryCode);
            } catch { setDefault(); }
          },
          setDefault,
          { enableHighAccuracy: true, timeout: 8000 }
        );
      } else {
        setDefault();
      }
    };

fetch('https://ipapi.co/json/')
  .then(r => r.json())
  .then(ipData => {
    const countryCode = ipData?.country_code?.toLowerCase();
    console.log('IP-based country:', countryCode, ipData?.country_name);
    // Always apply — applyCountryCode handles unmapped → USD fallback internally
    applyCountryCode(countryCode);
  })
  .catch(() => {
    console.log('IP lookup failed, falling back to GPS');
    tryGPS();
  });
  }
}, [currencies, selectedCurrency, dispatch]);
  if (loading) return (
    <div className="cd-shimmer px-3 py-2 w-20 h-8" />
  );

  if (error) return null;

  /* ── Render ── */
  return (
    <div className="relative flex-shrink-0" ref={dropdownRef} style={{ fontFamily: "'Montserrat', sans-serif" }}>

      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "cd-trigger flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold select-none outline-none",
          "transition-all duration-200",
          shouldBeWhite
            ? [
                "border",
                isOpen
                  ? "bg-[#f5ede4] border-[#c9a080] text-[#6b4226] shadow-[0_2px_12px_rgba(168,112,78,0.18)]"
                  : "bg-white border-stone-200 text-stone-700 hover:border-[#c9a080] hover:bg-[#fdf8f4] hover:text-[#6b4226] shadow-sm hover:shadow-[0_2px_12px_rgba(168,112,78,0.12)]"
              ].join(' ')
            : [
                "border border-white/20",
                isOpen
                  ? "bg-white/20 text-white"
                  : "text-white/80 hover:bg-white/15 hover:text-white"
              ].join(' ')
        )}
      >
        {selectedCurrency ? (
          <>
            <span
              className="cd-trigger-inner flex items-center justify-center w-5 h-5 rounded-md text-[12px] font-bold leading-none flex-shrink-0"
              style={{
                background: shouldBeWhite ? (isOpen ? '#a8704e' : '#e8d5c4') : 'rgba(255,255,255,0.2)',
                color: shouldBeWhite ? (isOpen ? '#fff' : '#7a4f35') : '#fff',
              }}
            >
              {selectedCurrency.symbol}
            </span>
            <span className="uppercase tracking-widest text-[10px]">
              {selectedCurrency.name}
            </span>
          </>
        ) : (
          <span className="uppercase tracking-widest text-[10px] text-stone-400">Currency</span>
        )}
        <ChevronDown
          className={`cd-chevron w-3 h-3 opacity-50 ${isOpen ? 'open' : ''}`}
        />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className="cd-panel absolute top-[calc(100%+8px)] right-0 min-w-[160px] bg-white rounded-2xl z-[999] overflow-hidden"
          style={{
            boxShadow: '0 20px 60px rgba(100,60,30,0.16), 0 4px 16px rgba(100,60,30,0.08)',
            border: '1px solid rgba(210,185,165,0.4)',
          }}
        >
          {/* Header */}
          <div
            className="px-4 pt-3.5 pb-2.5"
            style={{
              background: 'linear-gradient(135deg, #fdf7f2 0%, #f5ede4 100%)',
              borderBottom: '1px solid rgba(210,185,165,0.3)',
            }}
          >
            <p
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-stone-400"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '11px', fontWeight: 600, fontStyle: 'italic', letterSpacing: '0.12em' }}
            >
              Select Currency
            </p>
          </div>

          {/* List */}
          <div className="cd-scroll py-1.5 max-h-56 overflow-y-auto">
            {currencies.map((currency, index) => {
              const isSelected = selectedCurrency?.name === currency.name;
              return (
                <button
                  key={index}
                  onClick={() => handleSelect(currency)}
                  className={cn(
                    "cd-item w-full px-3.5 py-2.5 text-left flex items-center justify-between gap-2 group",
                    isSelected ? "bg-[#fdf0e6]" : "hover:bg-[#fdf7f3]"
                  )}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className="cd-symbol w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold"
                      style={
                        isSelected
                          ? { background: 'linear-gradient(135deg, #c9845a, #a8704e)', color: '#fff', boxShadow: '0 3px 10px rgba(168,112,78,0.35)' }
                          : { background: '#f0e8e0', color: '#9a6b4e' }
                      }
                    >
                      {currency.symbol}
                    </div>
                    <div className="min-w-0">
                      <p
                        className={cn(
                          "text-[11px] font-bold uppercase tracking-[0.1em] truncate",
                          isSelected ? "text-[#7a4a2a]" : "text-stone-600 group-hover:text-stone-900"
                        )}
                      >
                        {currency.name}
                      </p>
                      {currency.country_name && (
                        <p className="text-[9px] text-stone-400 truncate tracking-wide mt-0.5">
                          {currency.country_name}
                        </p>
                      )}
                    </div>
                  </div>

                  {isSelected ? (
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #c9845a, #a8704e)', boxShadow: '0 2px 6px rgba(168,112,78,0.4)' }}>
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-stone-200 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer accent line */}
          <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #e8c9ad, #c9845a, #e8c9ad)' }} />
        </div>
      )}
    </div>
  );
};

export default CurrencyDropdown;